import { Badge, Box, Center, Flex, Heading, Image, Spinner, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { FiMapPin, FiClock } from 'react-icons/fi';
import EventsService from '../../../../services/events/eventsService';
import EventParticipantsCardList from '../../components/EventParticipantsCardList/EventParticipantsCardList';
import { useAppToast } from '../../../../hooks/useAppToast';
import { BASE_URL } from '../../../../config';

const EventParticipantsPage = () => {
    const { id } = useParams();
    const { t } = useTranslation('default');
    const navigate = useNavigate();
    const toast = useAppToast();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const res = await EventsService.getEvent(id);
                setEvent(res.data.data);
            } catch {
                toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();
    }, [id]);

    if (loading) {
        return (
            <Center w="100%" py="80px">
                <Spinner size="xl" color="brand.500" />
            </Center>
        );
    }

    const coverImgSrc = event?.coverImage ? `${BASE_URL}/${event.coverImage}` : null;

    const formatDateRange = () => {
        if (!event) return '';
        const start = `${moment(event.startDate).format('D.MM.YYYY')} ${event.startTime}`;
        if (event.startDate === event.endDate) {
            return `${start} — ${event.endTime}`;
        }
        return `${start} — ${moment(event.endDate).format('D.MM.YYYY')} ${event.endTime}`;
    };

    return (
        <Flex w="100%" maxW="7xl" flexDirection="column" px={{ base: 4, md: 4 }} gap="32px" py={{ base: '40px', md: '80px' }}>

            {/* Nav */}
            <Flex
                w="100%"
                maxW="7xl"
                className="nav-wrapper"
                gap="16px"
                align="center"
            >
                <Flex gap="16px" align="center">
                    <Flex onClick={() => navigate('/')} cursor="pointer" align="center" flexShrink={0}>
                        <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 15.5L1 8.5M1 8.5L8 1.5M1 8.5H19" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Flex>
                    <Text fontFamily="heading" fontWeight="700" fontSize={{ base: "26px", md: "30px" }} letterSpacing="-0.6px" lineHeight="1.1" color="#1E1517">
                        {t('event.participants.title')}
                    </Text>
                </Flex>
            </Flex>

            {/* Event banner — okładka zawsze w proporcji 16:9 */}
            {event && (
                <Flex bg="surface.card" border="1px solid" borderColor="surface.border" borderRadius="20px" p={{ base: '12px', md: '16px' }}
                    gap={{ base: '14px', md: '24px' }} direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }}>
                    <Box w={{ base: '100%', md: '320px' }} flexShrink={0} borderRadius="14px" overflow="hidden" bg="brand.50" style={{ aspectRatio: '16 / 9' }}>
                        {coverImgSrc && (
                            <Image src={coverImgSrc} alt={event.title} w="100%" h="100%" objectFit="contain" bg="#F8ECEF" />
                        )}
                    </Box>
                    <Flex direction="column" gap="10px" px={{ base: '4px', md: 0 }} minW={0}>
                        {event.categories?.length > 0 && (
                            <Flex gap="6px" flexWrap="wrap">
                                {event.categories.map((cat, i) => (
                                    <Badge key={i} bg="brand.100" color="brand.600" fontWeight="600" fontSize="12px"
                                        px="10px" py="3px" borderRadius="full" textTransform="none">
                                        {cat}
                                    </Badge>
                                ))}
                            </Flex>
                        )}
                        <Heading as="h2" fontWeight={700} fontSize={{ base: '22px', md: '26px' }} lineHeight="1.15" letterSpacing="-0.4px" wordBreak="break-word">
                            {event.title}
                        </Heading>
                        {event.address && (
                            <Flex align="flex-start" gap="8px">
                                <Box as={FiMapPin} color="brand.500" boxSize="16px" mt="2px" flexShrink={0} />
                                <Text color="ink.600" fontSize="14px" lineHeight="1.4">{event.address}</Text>
                            </Flex>
                        )}
                        <Flex align="center" gap="8px">
                            <Box as={FiClock} color="brand.500" boxSize="16px" flexShrink={0} />
                            <Text color="ink.600" fontSize="14px">{formatDateRange()}</Text>
                        </Flex>
                    </Flex>
                </Flex>
            )}

            {/* Participants table */}
            {event && (
                <EventParticipantsCardList
                    eventId={event.id}
                    isEditing={true}
                    eventName={event.title}
                    eventData={event}
                />
            )}
        </Flex>
    );
};

export default EventParticipantsPage;
